/**
 * PortfolioPremiumTemplate — Editorial, raw, awwwards-grade for creatives.
 * Palette: bone white (#F5F1EB) + deep black ink (#0F0F0F) + orange accent (#FF5722)
 * Fonts: Unbounded (display) + Chivo (body) — bold & modern typo.
 */
import EditableText from "./EditableText";
import type { WebsiteCustomizations } from "./PremiumTemplate";

interface Props {
  name: string;
  desc: string;
  product: string;
  city: string;
  customizations?: WebsiteCustomizations;
  editMode?: boolean;
  onCustomize?: (field: keyof WebsiteCustomizations, value: string) => void;
}

const c = (f: keyof WebsiteCustomizations, fb: string, cu?: WebsiteCustomizations) => cu?.[f] || fb;

const PortfolioPremiumTemplate = ({ name, desc, product, city, customizations, editMode, onCustomize }: Props) => {
  const set = (f: keyof WebsiteCustomizations) => (v: string) => onCustomize?.(f, v);

  return (
    <div
      className="min-h-screen relative overflow-x-hidden"
      style={{ background: "#F5F1EB", color: "#0F0F0F", fontFamily: "'Chivo', sans-serif" }}
      data-testid="template-portfolio-premium"
    >
      {/* Nav */}
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-black">
        <EditableText
          as="div"
          value={name || "Atelier NOIR"}
          onChange={set("logoText")}
          editMode={editMode}
          data-field="logoText"
          style={{ fontFamily: "'Unbounded', sans-serif", fontSize: "20px", fontWeight: 700 }}
        />
        <nav className="hidden md:flex items-center gap-10 text-xs uppercase tracking-[0.2em] font-bold">
          <span className="cursor-pointer hover:text-[#FF5722] transition-colors">Index</span>
          <span className="cursor-pointer hover:text-[#FF5722] transition-colors">Studio</span>
          <span className="cursor-pointer hover:text-[#FF5722] transition-colors">Journal</span>
          <span className="cursor-pointer hover:text-[#FF5722] transition-colors">Contact</span>
        </nav>
        <div className="text-xs font-mono font-bold">{(new Date()).toLocaleDateString("fr", { day: "2-digit", month: "2-digit", year: "2-digit" })}</div>
      </header>

      {/* Mega Hero */}
      <section className="px-6 md:px-12 py-16 md:py-20 border-b border-black">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center gap-3 mb-8 text-xs uppercase tracking-[0.25em] font-bold">
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#FF5722" }} />
            Available for new projects · Q2 2026
          </div>
          <EditableText
            as="h1"
            value={c("heroTitle", name || "DESIGN.\nSCALE.\nMULTIPLY.", customizations)}
            onChange={set("heroTitle")}
            editMode={editMode}
            data-field="heroTitle"
            className="whitespace-pre-line leading-[0.85]"
            style={{
              fontFamily: "'Unbounded', sans-serif",
              fontSize: "clamp(4rem, 14vw, 15rem)",
              fontWeight: 900,
              letterSpacing: "-0.05em",
              textTransform: "uppercase",
            }}
          />
          <div className="mt-12 grid md:grid-cols-3 gap-6 items-start">
            <div className="md:col-span-2">
              <EditableText
                as="p"
                value={c("heroSubtitle", desc || "Studio de branding indépendant. On conçoit des identités qui pèsent dans la culture — pas juste des moodboards Instagram.", customizations)}
                onChange={set("heroSubtitle")}
                editMode={editMode}
                data-field="heroSubtitle"
                multiline
                className="text-xl md:text-2xl leading-tight max-w-2xl"
                style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 400 }}
              />
            </div>
            <div className="flex items-center gap-2 md:justify-end">
              <EditableText
                as="button"
                value={c("ctaText", "See projects ↗", customizations)}
                onChange={set("ctaText")}
                editMode={editMode}
                data-field="ctaText"
                className="px-8 py-5 border-2 border-black bg-black text-[#F5F1EB] hover:bg-[#FF5722] hover:border-[#FF5722] transition-all uppercase font-bold tracking-[0.2em] text-xs"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Project Grid — asymmetric bento */}
      <section className="px-6 md:px-12 py-16 border-b border-black">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-baseline justify-between mb-12">
            <div className="text-xs uppercase tracking-[0.3em] font-bold opacity-60">— Selected · 06 of 43</div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold hover:text-[#FF5722] cursor-pointer">Full index ↗</div>
          </div>
          <div className="grid grid-cols-12 grid-rows-[auto_auto_auto] md:grid-rows-[minmax(280px,auto)_minmax(280px,auto)] gap-4 md:gap-6">
            {[
              { span: "col-span-12 md:col-span-8 md:row-span-2", n: "01", t: "Kōzō", tag: "Brand · Packaging", bg: "linear-gradient(135deg, #FF5722 0%, #0F0F0F 100%)", size: "clamp(3rem, 6vw, 5.5rem)", color: "#F5F1EB" },
              { span: "col-span-6 md:col-span-4", n: "02", t: "AKOYA", tag: "Art Direction", bg: "#0F0F0F", size: "40px", color: "#F5F1EB" },
              { span: "col-span-6 md:col-span-4", n: "03", t: "Verre", tag: "Typography", bg: "#E5DDD0", size: "40px", color: "#0F0F0F" },
              { span: "col-span-6 md:col-span-6", n: "04", t: "Noir Lumière", tag: "Editorial · Print", bg: "#FF5722", size: "42px", color: "#F5F1EB" },
              { span: "col-span-6 md:col-span-6", n: "05", t: "HÉLIX", tag: "Motion · 3D", bg: "#0F0F0F", size: "42px", color: "#F5F1EB" },
            ].map((p, i) => (
              <div
                key={i}
                className={`${p.span} relative group cursor-pointer overflow-hidden border border-black/10`}
                style={{ background: p.bg, color: p.color, minHeight: 240 }}
              >
                <div className="absolute inset-0 p-5 md:p-7 flex flex-col justify-between">
                  <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] font-bold opacity-70">
                    <span>#{p.n}</span>
                    <span>{p.tag}</span>
                  </div>
                  <div style={{ fontFamily: "'Unbounded', sans-serif", fontSize: p.size, fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.9 }} className="transition-transform duration-700 group-hover:-translate-y-1">
                    {p.t}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About split */}
      <section className="px-6 md:px-12 py-20 border-b border-black">
        <div className="max-w-[1600px] mx-auto grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.3em] font-bold opacity-60">— Studio</div>
            <div className="mt-4" style={{ fontFamily: "'Unbounded', sans-serif", fontSize: "48px", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.95 }}>
              Small.<br />
              <span style={{ color: "#FF5722" }}>Sharp.</span><br />
              Rigoureux.
            </div>
          </div>
          <div className="md:col-span-8 text-lg md:text-xl leading-relaxed max-w-3xl">
            <p className="mb-6">
              Trois disciplines, six cerveaux, une obsession : faire en sorte que nos clients soient VUS. Nous intervenons de la stratégie de marque
              à l'expérience digitale, avec une exigence qui fait peur aux studios 10x plus gros que nous.
            </p>
            <p className="opacity-70">
              Basés à {city || "Paris"}. Bureaux de représentation à Tokyo et New York. On voyage, on dort parfois — jamais au même endroit deux mois de suite.
            </p>
            <div className="mt-8 flex items-center gap-8 text-xs uppercase tracking-[0.25em] font-bold">
              <span>ADC Winner '24</span>
              <span>•</span>
              <span>Awwwards SOTD ×7</span>
              <span>•</span>
              <span>D&AD '23</span>
            </div>
          </div>
        </div>
      </section>

      {/* Cinematic CTA */}
      <section className="px-6 md:px-12 py-32 text-center" style={{ background: "#0F0F0F", color: "#F5F1EB" }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-xs uppercase tracking-[0.4em] font-bold opacity-60 mb-8">— Next project?</div>
          <div style={{ fontFamily: "'Unbounded', sans-serif", fontSize: "clamp(3rem, 8vw, 7rem)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.9 }}>
            LET'S<br />
            <span style={{ color: "#FF5722" }}>MAKE IT</span><br />
            HAPPEN.
          </div>
          <div className="mt-12">
            <EditableText
              as="a"
              value={c("email", "hello@ateliernoir.studio", customizations)}
              onChange={set("email")}
              editMode={editMode}
              data-field="email"
              className="inline-block px-10 py-5 border-2 border-[#F5F1EB] hover:bg-[#FF5722] hover:border-[#FF5722] transition-all uppercase font-bold tracking-[0.25em] text-sm"
            />
          </div>
        </div>
      </section>

      {/* Footer minimal */}
      <footer className="px-6 md:px-12 py-8 flex items-center justify-between text-xs uppercase tracking-[0.2em] font-bold opacity-70">
        <span>© 2026 {name || "Atelier NOIR"} · Tous droits réservés</span>
        <span>{product || city || "Paris · Tokyo · New York"}</span>
      </footer>
    </div>
  );
};

export default PortfolioPremiumTemplate;
